import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Slide,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { CrossIcon } from "../../assets/icons";
import LanguageSwitcher, { Language } from "../LanguageSwitcher";
import {
  CreatePlanOptionRequest,
  PlanDetailPlanOptionResponse,
  PlanDetailPlanOptionResponseMealTypeListEnum,
} from "../../openapi/client";
import { IDialogProps } from "../../openapi/interfaces";
import { checkEmpty, checkIf, DayList, generateRandom, slugify } from "../../utils";
import { RadioBoxCustom } from "../RadioBox";
import Tagging from "../Tagging";
import Transition from "../Transition";
import useValidation from "../Validation";
import Api from "../../openapi";
import { useDispatch } from "react-redux";
import { setNotification } from "../../store/notificationSlice";
import { LoadingButton } from "@mui/lab";
import { Toggle } from "../Switch";
import useMealTypes from "../../hooks/useMealTypes";
import InputNumber from "../InputNumber";
import InputRange from "../InputRange";

interface IPlanOptionCrud extends IDialogProps {
  planOption?: PlanDetailPlanOptionResponse;
  planId?: string;
  planPeriod?: number;
}
interface UpdatePlanOptionRequest extends CreatePlanOptionRequest {
  id?: string;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 516,
    },
  },
};

function getStyles(name: string, personName: readonly string[]) {
  return {
    fontWeight: personName.indexOf(name) === -1 ? 400 : 700,
  };
}

export default function PlanOption(props: IPlanOptionCrud) {
  const [formDirty, setFormDirty] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<Language>("en");
  const [planOption, setPlanOptionEdit] = useState<any>({
    name: { en: "", ar: "" },
    selection: { en: "", ar: "" },
    mealTypeList: [],
    period: "",
    dailyPrice: "",
    highlights: { en: "", ar: "" },
    active: true,
    visible: false,
  });
  const dispatch = useDispatch();
  const mealTypeList = useMealTypes();
  const { validation, setValidation } = useValidation(planOption);
  const [loading, setLoading] = useState(false);
  const [mealTypeListSelected, setMealTypeListSelected] = useState<string[]>([]);
  const [explicitPeriodOption, setExplicitPeriodOption] = useState({ text: "", value: 1 });

  const handleFieldChange = (field: any) => {
    if (field.highlights) {
      if (field.highlights.en) {
        field.highlights.en = field.highlights.en.replace(/\n{2,}/g, "\n"); // Replace 2+ newlines with single newline
      }
      if (field.highlights.ar) {
        field.highlights.ar = field.highlights.ar.replace(/\n{2,}/g, "\n"); // Replace 2+ newlines with single newline
      }
    }
    setFormDirty(true);
    setPlanOptionEdit((state: any) => ({ ...state, ...field }));
  };

  const cleanHighlights = (text: string) => {
    return text.replace(/^\n+|\n+$/g, ""); // Remove leading and trailing newlines only
  };
  // console.log("mealTypeList server", mealTypeList);
  // console.log("planOption", planOption);
  // console.log("dayList", dayList);
  // console.log("mealTypeList", mealTypeListSelected);

  const handleMealListChange = (event: SelectChangeEvent<typeof mealTypeListSelected>) => {
    const {
      target: { value },
    } = event;
    setMealTypeListSelected(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  // console.log("planOption", planOption);
  // console.log("portions", portions);
  // console.log("validation", validation);

  const saveChanges = async () => {
    if (validation.error || planOption.period < explicitPeriodOption.value) {
      setValidation((state) => ({ ...state, show: true }));
      return;
    }

    let PlanOptionReqDto: UpdatePlanOptionRequest = {
      ...planOption,
      highlights: {
        en: checkEmpty(planOption.highlights?.en) ? null : planOption.highlights.en,
        ar: checkEmpty(planOption.highlights?.ar) ? null : planOption.highlights.ar,
      },
      dailyPrice: Number(planOption.dailyPrice),
      period: Number(planOption.period),
      mealTypeList: planOption.mealTypeList.map((i: string) => mealTypeList.find((li) => li.description === i)?.name),
    };

    try {
      setLoading(true);
      let message;
      if (props.create) {
        message = "Plan option has been created successfully";
        await Api.PlanOption.createPlanOption(String(props.planId), PlanOptionReqDto);
        resetForm();
      } else if (props.edit) {
        message = "Plan option has been updated successfully";
        const { id, ...UpdatePlanReqDto } = PlanOptionReqDto;
        await Api.PlanOption.updatePlanOption(String(id), UpdatePlanReqDto);
      }
      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message,
        })
      );
      props.onConfirm(true);
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
    setLoading(false);
  };

  const resetForm = () => {
    setSelectedLanguage("en");
    setPlanOptionEdit({
      name: { en: "", ar: "" },
      selection: { en: "", ar: "" },
      period: "",
      dailyPrice: "",
      highlights: { en: "", ar: "" },
      mealTypeList: [],
      active: true,
      visible: false,
    });
    setMealTypeListSelected([]);
    setValidation((state) => ({ ...state, show: false }));
  };

  const handlePanelClose = (...args: any) => {
    resetForm();
    props.onClose(args);
  };

  useEffect(() => {
    if (props.planOption) {
      setPlanOptionEdit({
        ...props.planOption,
      });
      setMealTypeListSelected(
        Array.from(props.planOption?.mealTypeList as Set<PlanDetailPlanOptionResponseMealTypeListEnum>).map((i) =>
          String(mealTypeList.find((li) => li.name === i)?.description)
        )
      );
      setExplicitPeriodOption({
        text: `Minimum number of delivery days for this plan is ${Number(props.planPeriod)}`,
        value: Number(props.planPeriod),
      });
    }
  }, [props.planOption]);

  useEffect(() => {
    setPlanOptionEdit((state: any) => ({
      ...state,
      mealTypeList: mealTypeListSelected,
    }));
  }, [mealTypeListSelected]);

  return (
    <Fragment>
      <Dialog
        open={props.open}
        TransitionComponent={Transition}
        onClose={handlePanelClose}
        aria-describedby="alert-dialog-slide-description"
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "16px",
            boxShadow: "0px 12px 24px 0px rgba(0, 0, 0, 0.15)",
            padding: "24px",
            width: "564px",
            maxWidth: "564px",
          },
          "& .MuiDialogTitle-root": {
            padding: "0 48px 16px 0",
          },
          "& .MuiDialogContent-root,& .MuiDialogActions-root": {
            padding: 0,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "text.dark",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography component="span" sx={{ fontSize: "16px", fontWeight: 500 }}>
            {props.title}
          </Typography>
          <LanguageSwitcher value={selectedLanguage} onChange={setSelectedLanguage} />
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handlePanelClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "text.fontLight2",
          }}
        >
          <CrossIcon />
        </IconButton>
        <DialogContent>
          <FormControl sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}>
              <Box sx={{ display: "flex", columnGap: "12px", alignItems: "baseline" }}></Box>
              <Box className="form-section">
                <Box sx={{ display: "flex" }}>
                  <Typography component="span" className="form-label">
                    Option Name {selectedLanguage === "en" ? "(English)" : "(Arabic)"}
                  </Typography>
                  <Typography component="span" className="required-label">
                    {selectedLanguage === "en" ? "*" : ""}
                  </Typography>
                </Box>

                <TextField
                  className="form-field"
                  value={planOption?.name?.[selectedLanguage] || ""}
                  error={validation.show && validation.name?.[selectedLanguage]?.error}
                  helperText={
                    validation.show && validation.name?.[selectedLanguage]?.error
                      ? validation.name[selectedLanguage]?.helperText
                      : undefined
                  }
                  onChange={(e) =>
                    handleFieldChange({
                      name: { ...planOption.name, [selectedLanguage]: e.target.value },
                    })
                  }
                  inputProps={{
                    dir: selectedLanguage === "ar" ? "rtl" : "ltr",
                  }}
                />
              </Box>
              <Box className="form-section">
                <Box sx={{ display: "flex" }}>
                  <Typography component="span" className="form-label">
                    Selection Name (Visible Title) {selectedLanguage === "en" ? "(English)" : "(Arabic)"}
                  </Typography>
                  <Typography component="span" className="required-label">
                    {selectedLanguage === "en" ? "*" : ""}
                  </Typography>
                </Box>

                <TextField
                  className="form-field"
                  value={planOption?.selection?.[selectedLanguage] || ""}
                  error={validation.show && validation.selection?.[selectedLanguage]?.error}
                  helperText={
                    validation.show && validation.selection?.[selectedLanguage]?.error
                      ? validation.selection[selectedLanguage]?.helperText
                      : undefined
                  }
                  onChange={(e) =>
                    handleFieldChange({
                      selection: { ...planOption.selection, [selectedLanguage]: e.target.value },
                    })
                  }
                  inputProps={{
                    dir: selectedLanguage === "ar" ? "rtl" : "ltr",
                  }}
                />
              </Box>
              <Box className="form-section">
                <Box sx={{ display: "flex" }}>
                  <Typography component="span" className="form-label">
                    Which meal types to add
                  </Typography>
                  <Typography component="span" className="required-label">
                    *
                  </Typography>
                </Box>

                <FormControl sx={{ m: 0, width: "100%" }}>
                  <Select
                    multiple
                    value={mealTypeListSelected}
                    onChange={handleMealListChange}
                    input={<OutlinedInput sx={{ height: "auto", marginBottom: 0 }} />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} />
                        ))}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    {mealTypeList.map((i) => (
                      <MenuItem
                        key={i.name}
                        value={i.description}
                        style={getStyles(String(i.name), mealTypeListSelected)}
                      >
                        {i.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {validation.show && validation.mealTypeList?.error ? (
                  <FormHelperText sx={{ color: "text.error", margin: 0 }}>
                    {validation.mealTypeList.helperText}
                  </FormHelperText>
                ) : null}
              </Box>

              <Box sx={{ display: "flex", columnGap: "12px", alignItems: "baseline" }}>
                <Box className="form-section">
                  <Box sx={{ display: "flex" }}>
                    <Typography component="span" className="form-label">
                      Period (Days)
                    </Typography>
                    <Typography component="span" className="required-label">
                      *
                    </Typography>
                  </Box>

                  <InputRange value={planOption.period} onChange={(period) => handleFieldChange({ period })} />
                  {validation.show &&
                  (validation.period?.error || planOption.period < Number(explicitPeriodOption.value)) ? (
                    <FormHelperText sx={{ color: "text.error", margin: 0 }}>
                      {planOption.period < explicitPeriodOption.value
                        ? explicitPeriodOption.text
                        : validation.period?.helperText}
                    </FormHelperText>
                  ) : null}
                </Box>
                <Box className="form-section">
                  <Box sx={{ display: "flex" }}>
                    <Typography component="span" className="form-label">
                      Daily Price
                    </Typography>
                    <Typography component="span" className="required-label">
                      *
                    </Typography>
                  </Box>

                  <TextField
                    className="form-field"
                    value={planOption?.dailyPrice}
                    error={validation.show && validation.dailyPrice?.error}
                    helperText={
                      validation.show && validation.dailyPrice?.error ? validation.dailyPrice.helperText : undefined
                    }
                    onChange={(e) => handleFieldChange({ dailyPrice: e.target.value })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography sx={{ color: "text.fontLight3", fontSize: "14px", fontWeight: 500 }}>
                            QAR
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>

              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Highlights (Optional) {selectedLanguage === "en" ? "(English)" : "(Arabic)"}
                </Typography>
                <TextField
                  className="form-field"
                  placeholder="Enter highlights seperated by each new line, will be shown as bullet points"
                  multiline
                  rows={4}
                  sx={{ "& .MuiInputBase-root": { height: "auto" } }}
                  value={planOption?.highlights?.[selectedLanguage] || ""}
                  onChange={(e) =>
                    handleFieldChange({
                      highlights: { ...planOption.highlights, [selectedLanguage]: e.target.value },
                    })
                  }
                  onBlur={(e) =>
                    handleFieldChange({
                      highlights: { ...planOption.highlights, [selectedLanguage]: cleanHighlights(e.target.value) },
                    })
                  }
                  inputProps={{
                    dir: selectedLanguage === "ar" ? "rtl" : "ltr",
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                <Box className="form-section">
                  <FormControlLabel
                    sx={{
                      margin: 0,
                      columnGap: "8px",
                      "& .MuiFormControlLabel-label": { fontSize: "14px", fontWeight: 500 },
                    }}
                    control={
                      <Toggle
                        checked={Boolean(planOption?.active)}
                        onChange={(e) => handleFieldChange({ active: Boolean(e.target.checked) })}
                      />
                    }
                    label="Make the plan option active"
                  />
                  <FormControlLabel
                    sx={{
                      margin: 0,
                      columnGap: "8px",
                      "& .MuiFormControlLabel-label": { fontSize: "14px", fontWeight: 500 },
                    }}
                    control={
                      <Toggle
                        checked={Boolean(planOption?.visible)}
                        onChange={(e) => handleFieldChange({ visible: Boolean(e.target.checked) })}
                      />
                    }
                    label="Make the plan option visible"
                  />
                </Box>
              </Box>
            </Box>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <LoadingButton
            fullWidth
            loading={loading}
            color="secondary"
            variant="contained"
            sx={{ display: "flex", flexDirection: "column", marginTop: "16px" }}
            onClick={saveChanges}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}
